import { useSelector } from 'react-redux';
import TitleHeader from '../../components/MasterComponent/TitleHeader/TitleHeader';
import { SelectedAccount } from '../../components/SelectAccount/SelectAccount';
import { CLOUD_PROVIDER_SERVICES } from '../../utils/constants';
import { useFetchData } from '../../hooks/useFetchData';
import { config } from './config';
import { exportToFile } from '../../utils/formateXLSX';
import { accountState, RootState } from '../../store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../CostBreakup/cost-breakup.css';
import Header from '../../components/CostBreakup/Header';
import { MultilevelHeaderTable } from '../../components/CostBreakup/MultilevelHeaderTable';
import { ALL_VALUE } from '../Invoice/config';
import {
  COVERAGE_TABS,
  CoverageTabs,
  COVERAGE_SERVICE_REPORT_MAP,
} from './reservationTabs';
import { COVERAGE_SUMMABLE_COLUMNS_BY_TAB } from './reservationTableColumns';
import CustomSnackBar from '../../components/MasterComponent/CustomSnackbar/CustomSnackBar';

const Coverage: React.FC<any> = () => {
  const selectedAccountInfo = useSelector(accountState);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [coverageData, setCoverageData] = useState([]);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'info' | 'success' | 'error' | 'warning'
  >('info');
  const [selectedAccountId, setSelectedAccountId] = React.useState<any>(
    selectedAccountInfo.selectedAccountId
  );
  const [selectedTab, setSelectedTab] = useState<CoverageTabs>(
    COVERAGE_TABS[0]
  );
  const selectedMonth = useSelector(
    (state: RootState) => state.month.selectedMonth
  );
  const [selectedMonthName, selectedYear] = selectedMonth.split(' ');
  const currentTabRef = useRef<CoverageTabs>(selectedTab);
  const getCacheKey = (tab: CoverageTabs, accountId: string, month: string) =>
  `${tab}_${accountId}_${month}`;
  const cacheRef = useRef<Map<string, any>>(new Map());
  const monthNumber =
    new Date(`${selectedMonthName} 1, ${selectedYear}`).getMonth() + 1;
  const formattedMonth = monthNumber.toString().padStart(2, '0');
  const [sortColumn, setSortColumn] = useState<string>('');
  const reqBodyRef = useRef({
    startDate: `${selectedYear}-${formattedMonth}-01`,
    endDate: `${selectedYear}-${formattedMonth}-${new Date(
      parseInt(selectedYear),
      monthNumber,
      0
    ).getDate()}`,
    service: COVERAGE_SERVICE_REPORT_MAP[selectedTab],
    ...(selectedAccountId !== ALL_VALUE
      ? { accountId: selectedAccountId }
      : {}),
  });

  const { fetch, data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getCoverageReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  const handleSnackBarClose = () => {
    setSnackBarOpen(false);
  };

  const onAccountChange = (data: any) => {
    if (data.cloudType === CLOUD_PROVIDER_SERVICES.AWS)
      setSelectedAccountId(data.accountId);
  };

  // Handle sorting
  const handleSort = (column: string) => {
    const newDirection =
      sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

    setSortColumn(column);
    setSortDirection(newDirection);
  };

  const handleTabClick = (newTab: CoverageTabs) => {
    setSelectedTab(newTab);
  };

  const handleExcelDownload = () => {
    reqBodyRef.current = {
      startDate: `${selectedYear}-${formattedMonth}-01`,
      endDate: `${selectedYear}-${formattedMonth}-${new Date(
        parseInt(selectedYear),
        monthNumber,
        0
      ).getDate()}`,
      service: COVERAGE_SERVICE_REPORT_MAP[selectedTab],
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
    fetchCoverageReport();
  };

  const {
    fetch: fetchCoverageReport,
    data: fetchCoverageReportDownloadStatus,
    loader: isDownloadLoading,
    error: fetchCoverageReportDownloadError,
  } = useFetchData({
    fetchData: () => config.downloadCoverageReport(reqBodyRef.current),
    fetchOnMount: false,
  });

  // Function to format data keys from backend format to display format
  const formatDataForDisplay = useMemo(() => {
    if (!coverageData || coverageData.length === 0) return [];

    return coverageData.map((item: any) => {
      const formattedItem: any = {};

      // Transform each field key from snake_case to Title Case with spaces
      Object.keys(item).forEach((key) => {
        // Convert snake_case to Title Case with spaces
        const displayKey = key
          .split('_')
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');

        formattedItem[displayKey] = item[key];
      });

      return formattedItem;
    });
  }, [coverageData]);

  const sortedServiceEntries = useMemo(() => {
    // Convert array to entries format expected by MultilevelHeaderTable
    const serviceEntries = formatDataForDisplay.map(
      (item: any, index: number) => [`row_${index}`, item]
    ) as [string, Record<string, any>][];

    if (!sortColumn) return serviceEntries;

    return [...serviceEntries].sort((a, b) => {
      let valueA = a[1][sortColumn];
      let valueB = b[1][sortColumn];

      // Null / undefined fallback
      if (valueA === undefined || valueA === null) valueA = '';
      if (valueB === undefined || valueB === null) valueB = '';

      // If both are numbers
      if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
        return sortDirection === 'asc'
          ? Number(valueA) - Number(valueB)
          : Number(valueB) - Number(valueA);
      }

      // Otherwise compare as strings
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      return sortDirection === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
  }, [formatDataForDisplay, sortColumn, sortDirection]);

  const columns = useMemo(() => {
    if (!formatDataForDisplay || formatDataForDisplay.length === 0) return [];

    // Collect all unique keys across all entries
    const allKeysSet = new Set<string>();
    for (const item of formatDataForDisplay) {
      Object.keys(item).forEach((key) => allKeysSet.add(key));
    }

    return Array.from(allKeysSet).map((key) => ({
      key,
      label: key, // Use the display key directly since it's already formatted
    }));
  }, [formatDataForDisplay]);

  const summableColumns = useMemo(() => {
    const columns = COVERAGE_SUMMABLE_COLUMNS_BY_TAB[selectedTab] ?? [];
    return columns;
  }, [selectedTab]);

  useEffect(() => {
    const cacheKey = getCacheKey(selectedTab, selectedAccountId, selectedMonth);
    reqBodyRef.current = {
      startDate: `${selectedYear}-${formattedMonth}-01`,
      endDate: `${selectedYear}-${formattedMonth}-${new Date(
        parseInt(selectedYear),
        monthNumber,
        0
      ).getDate()}`,
      service: COVERAGE_SERVICE_REPORT_MAP[selectedTab],
      ...(selectedAccountId !== ALL_VALUE
        ? { accountId: selectedAccountId }
        : {}),
    };
    if (cacheRef.current.has(cacheKey)) {
      setCoverageData(cacheRef.current.get(cacheKey));
    } else {
      setCoverageData([]);
      fetch();
    }
  }, [selectedTab, selectedAccountId, selectedMonth]);

  let isCanceled = false;

  useEffect(() => {
    if (!isSubmitting && !isCanceled) {
      const cacheKey = getCacheKey(selectedTab, selectedAccountId, selectedMonth);
      if (reqBodyRef.current.service === COVERAGE_SERVICE_REPORT_MAP[currentTabRef.current]) {
        cacheRef.current.set(cacheKey, data ?? []);
        setCoverageData(data ?? []);
      }
    }

    return () => {
      isCanceled = true;
    };
  }, [data, isSubmitting, selectedTab, selectedAccountId, selectedMonth]);
  
  useEffect(() => {
      cacheRef.current.clear();
  }, [selectedAccountId, selectedMonth]);


  useEffect(() => {
    currentTabRef.current = selectedTab;
  }, [selectedTab]);

  useEffect(() => {
    if (!isDownloadLoading) {
      if (fetchCoverageReportDownloadError) {
        const errorMessage =
          fetchCoverageReportDownloadError?.error?.message ||
          'An error occurred while exporting.';
        setSnackBarMessage(errorMessage);
        setSnackBarOpen(true);
        setSnackbarSeverity('error');
      } else if (fetchCoverageReportDownloadStatus) {
        const {
          fileBuffer,
          fileName,
          message,
        } = fetchCoverageReportDownloadStatus;

        if (fileBuffer && fileName) {
          exportToFile(fileBuffer, fileName);
        } else if (message === 'No data available to export') {
          setSnackBarMessage('No data available to export.');
          setSnackBarOpen(true);
          setSnackbarSeverity('info');
        }
      }
    }
  }, [
    isDownloadLoading,
    fetchCoverageReportDownloadStatus,
    fetchCoverageReportDownloadError,
  ]);

  return (
    <div className="app-container">
      <CustomSnackBar
        message={snackBarMessage}
        onClose={handleSnackBarClose}
        open={snackBarOpen}
        severity={snackbarSeverity}
      />
      <TitleHeader
        extraContent={<SelectedAccount onAccountChange={onAccountChange} />}
      >
        Coverage
      </TitleHeader>
      <Header
        tabs={[...COVERAGE_TABS]}
        selectedTab={selectedTab}
        onTabChange={handleTabClick}
        showMonthFilter
        showExcelDownload
        onDownload={handleExcelDownload}
        isDownloading={isDownloadLoading}
      />
      <MultilevelHeaderTable
        loading={isSubmitting}
        sortedEntries={sortedServiceEntries}
        onSort={handleSort}
        columns={columns}
        summableColumns={summableColumns}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
      />
    </div>
  );
};

export default Coverage;
